import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { getCategoryName, formatViews } from "@/utils/movie";
import { useFavorites } from "@/hooks/useFavorites";

export default function MovieCard({ movie }: { movie: Movie }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(movie.id);

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-100 transition-colors hover:border-primary-500/60">
      <Link to={`/detail/${movie.id}`} className="block">
        <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
          <img
            src={movie.cover}
            alt={`${movie.title} 影片封面`}
            title={`${movie.title} - 三级影片`}
            className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-transparent opacity-80" />
          <span className="absolute left-2 top-2 rounded-md bg-primary-500/90 px-2 py-0.5 text-xs font-medium text-foreground-950">
            {movie.region}
          </span>
          <span className="absolute bottom-2 left-2 flex items-center gap-1 text-xs text-foreground-800">
            <i className="ri-star-fill text-accent-500" />
            {movie.rating.toFixed(1)}
          </span>
        </div>
      </Link>

      <button
        type="button"
        aria-label={favorite ? "取消收藏" : "收藏影片"}
        onClick={(event) => {
          event.preventDefault();
          toggleFavorite(movie.id);
        }}
        className={`absolute right-2 top-2 w-8 h-8 flex items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
          favorite
            ? "bg-accent-500 text-foreground-950"
            : "bg-background-50/70 text-foreground-800 hover:text-accent-500"
        }`}
      >
        <i className={`${favorite ? "ri-heart-fill" : "ri-heart-line"} text-base`} />
      </button>

      <div className="flex flex-1 flex-col gap-2 p-3 md:p-4">
        <Link to={`/detail/${movie.id}`}>
          <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 truncate hover:text-accent-500 transition-colors">
            {movie.title}
          </h3>
        </Link>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <span>{movie.year}</span>
          <span className="text-foreground-400">·</span>
          <Link to={`/category/${movie.categoryId}`} className="hover:text-accent-500 transition-colors">
            {getCategoryName(movie.categoryId)}
          </Link>
          <span className="text-foreground-400">·</span>
          <span className="flex items-center gap-1">
            <i className="ri-play-circle-line" />
            {formatViews(movie.views)}
          </span>
        </div>
        <p className="hidden sm:block text-xs leading-relaxed text-foreground-500 line-clamp-2">
          {movie.synopsis}
        </p>
      </div>
    </div>
  );
}